import { useMemo } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import { useForm, useFormField } from '@frontend/design-system';
import { useLocationTimezone } from '../../../../../../hooks';
import { isValidEndTime } from '../../../../helpers';
import { ReviewRequestReturnType } from './useReviewRequest';

export const useManageScheduleRequestModalForm = (
  selectedScheduleRequest: ScheduleRequest,
  isSourceFieldRequired: boolean,
  appointmentTypes: ReviewRequestReturnType['lists']['appointmentTypes']
) => {
  const { formatDateAndTime } = useLocationTimezone(selectedScheduleRequest.locationId);

  const scheduleRequestApprovalForm = useForm({
    fields: {
      appointmentType: {
        type: 'dropdown',
        value: '',
        required: true,
      },
      dateAndTime: {
        type: 'dropdown',
        value: selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime ?? '',
        required: true,
      },
      provider: {
        type: 'dropdown',
        value: '',
        required: true,
      },
      source: {
        type: 'dropdown',
        value: '',
        required: isSourceFieldRequired,
      },
      workstation: {
        type: 'dropdown',
        value: '',
      },
    },
  });

  const isCustomDateTime = scheduleRequestApprovalForm.values.dateAndTime === 'custom';

  const appointmentTypeDuration = useMemo(() => {
    const selectedApptType = appointmentTypes.find(
      (item) => item.id === scheduleRequestApprovalForm.values.appointmentType
    );
    return selectedApptType?.durationMinutes || 0;
  }, [appointmentTypes, scheduleRequestApprovalForm.values.appointmentType]);

  const customDateField = useFormField(
    {
      type: 'date',
      value: formatDateAndTime(selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime, 'MM/DD/YYYY'),
      required: isCustomDateTime,
    },
    [scheduleRequestApprovalForm.values.dateAndTime]
  );

  const customStartTimeField = useFormField(
    {
      type: 'time',
      value: formatDateAndTime(selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime, 'hh:mm a'),
      required: isCustomDateTime,
    },
    [scheduleRequestApprovalForm.values.dateAndTime]
  );

  const customEndTimeField = useFormField(
    {
      type: 'time',
      value: formatDateAndTime(
        dayjs(selectedScheduleRequest?.requestedOpenings?.[0]?.dateTime)
          .add(selectedScheduleRequest.duration ?? appointmentTypeDuration ?? 5, 'minutes')
          .utc(),
        'hh:mm a'
      ),
      required: isCustomDateTime,
    },
    [scheduleRequestApprovalForm.values.dateAndTime, appointmentTypeDuration, selectedScheduleRequest.duration]
  );

  const customDuration = useMemo(() => {
    const startTime = dayjs(`${customDateField.value} ${customStartTimeField.value}`);
    const endTime = dayjs(`${customDateField.value} ${customEndTimeField.value}`);
    return endTime.diff(startTime, 'minute');
  }, [customDateField.value, customStartTimeField.value, customEndTimeField.value]);

  const isValidCustomEndTime =
    isCustomDateTime &&
    isValidEndTime({
      date: customDateField.value,
      startTime: customStartTimeField.value,
      endTime: customEndTimeField.value,
    });

  return {
    scheduleRequestApprovalForm,
    customDateField,
    customStartTimeField,
    customEndTimeField,
    appointmentDuration: customDuration,
    isValidEndTime: isCustomDateTime ? isValidCustomEndTime : true,
  };
};
